// Install & update the CLI — everything about getting `cairn` onto a machine,
// signing in/out, and staying current. Sourced from specs/cli.md §1–2 and §7.

import { IconDownload, IconKey, IconRefresh } from "@tabler/icons-react";
import { serverOrigin } from "@/lib/server-origin";
import { CommandLine } from "@/components/command-line";
import { DocSection, K, P, Points } from "../doc-ui";

export function InstallCliDoc() {
  const origin = serverOrigin();

  return (
    <>
      <DocSection
        icon={IconDownload}
        title="Install"
        description="The installer is served by your own Cairn server — no third-party downloads."
      >
        <CommandLine command={`curl -fsSL ${origin}/install.sh | sh`} />
        <Points>
          <li>
            Installs to <K>~/.local/bin/cairn</K> — no sudo needed. Set <K>CAIRN_INSTALL_DIR</K> to
            install somewhere else.
          </li>
          <li>Supports macOS (Apple Silicon and Intel) and Linux x86_64.</li>
          <li>
            Every download is verified against a sha256 checksum from the server before it touches
            disk.
          </li>
        </Points>
      </DocSection>

      <DocSection
        icon={IconKey}
        title="Sign in and out"
        description="One login covers the whole machine: the CLI and every desktop Obsidian vault share it."
      >
        <CommandLine command={`cairn login --server ${origin}`} />
        <P>
          Enter your email and password (plus your TOTP code if you've enrolled two-factor). The
          session is stored in <K>~/.config/cairn/</K> (override with <K>CAIRN_CONFIG_DIR</K>), and
          the machine shows up as one device in your account's session list.
        </P>
        <CommandLine command="cairn whoami" comment="check who you're signed in as" />
        <CommandLine
          command="cairn logout"
          comment="sign this machine out — all its vaults stop syncing"
        />
        <P>
          There's no log-out button in the Obsidian plugin on desktop — the session belongs to the
          machine, so <K>cairn logout</K> is the one place to end it.
        </P>
      </DocSection>

      <DocSection
        icon={IconRefresh}
        title="Update"
        description="Updates come from the same server you signed in to, checksum-verified."
      >
        <CommandLine command="cairn version" comment="CLI + server versions, update availability" />
        <CommandLine command="cairn update" comment="self-update the CLI" />
        <CommandLine
          command="cairn plugin update"
          comment="update the plugin in every linked vault"
        />
        <P>
          <K>cairn plugin update &lt;path&gt;</K> updates just one vault's plugin instead of all of
          them.
        </P>
      </DocSection>
    </>
  );
}
